import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { SharedComponentsModule } from '../shared-components.module'
import { CommonModule } from '@angular/common'
import { TableAction } from './table-types/TableAction'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatCardModule } from '@angular/material/card'
import { TableHeader } from './table-types/TableHeader'
import { MenuButtonComponent } from '../menu-button/menu-button.component'
import { FiltersConfig } from './table-types/FilterConfig'
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator'
import { FormsModule } from '@angular/forms'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatSort, MatSortModule, SortDirection } from '@angular/material/sort'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

@Component({
  selector: 'app-generic-table',
  imports: [
    SharedComponentsModule,
    MatTableModule,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MenuButtonComponent,
    MatPaginator,
    MatPaginatorModule,
    MatTooltipModule,
    MatSortModule
  ],
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.css'
})
export class GenericTableComponent<T> {
  constructor(private sanitizer: DomSanitizer) {}

  /**
   * La proprietà `data` contiene i dati da visualizzare nella tabella.
   * È un array di oggetti di tipo generico `T[]`, dove `T` è il tipo dei dati
   * che il componente si aspetta. Ogni elemento dell'array rappresenta una riga
   * della tabella.
   */
  @Input() data: T[] = []

  /**
   * La proprietà `displayedColumns` è una mappa che associa le chiavi delle colonne
   * ai nomi visualizzati nell'intestazione della tabella.
   * La chiave della mappa è il nome della proprietà dell'oggetto da visualizzare,
   * mentre il valore è l'etichetta da mostrare nell'intestazione della tabella.
   */
  @Input() displayedColumns: Map<string, string> = new Map()

  /**
   * La proprietà `actions` è un array di oggetti `TableAction`, ognuno dei quali
   * definisce un'azione che può essere eseguita su una riga della tabella. Ogni
   * oggetto contiene un'etichetta (label), un'icona e una funzione `action` che
   * viene chiamata quando l'azione è eseguita.
   */
  @Input() actions?: TableAction<T>[] = []

  /**
   * La proprietà `header` definisce un'intestazione opzionale da visualizzare sopra la tabella.
   * È di tipo `TableHeader` e consente di mostrare:
   *
   * - un titolo (`title`), visualizzato come intestazione principale,
   * - una serie di pulsanti (`buttons`), ognuno dei quali può eseguire una determinata azione,
   *
   * Ogni pulsante è definito dal tipo `TableHeaderActions` e include:
   * - un'etichetta (`label`): il testo visualizzato nel pulsante,
   * - una funzione (`action`): la callback invocata al click del pulsante. Accetta un numero variabile di argomenti di tipo `string`,
   * - una funzione (`showCondition`): restituisce un booleano che determina se il pulsante deve essere visualizzato o meno.
   *
   * In aggiunta, un pulsante può rappresentare un **menu a discesa**, se:
   * - `isMenu` è impostato a `true`: indica che il pulsante deve comportarsi come un menu,
   * - `menuItems` è valorizzato con un array di voci (`MenuItems`), ciascuna con:
   *   - un'etichetta (`label`): il testo della voce di menu,
   *   - una funzione (`action`): la callback eseguita al click della voce del menu.
   *
   * Se `header` è definito, viene mostrata una sezione sopra la tabella che include il titolo e i relativi pulsanti o menu.
   */
  @Input() header?: TableHeader

  /**
   * Il getter `allColumns` restituisce un array di tutte le colonne da visualizzare
   * nella tabella, comprese le colonne definite in `displayedColumns` e la colonna
   * statica "actions" (per le azioni sui dati).
   * Questo array è utilizzato per configurare le righe della tabella.
   */
  get allColumns() {
    const columns = Array.from(this.displayedColumns.keys())
    if (this.actions && this.actions.length > 0) {
      columns.push('actions')
    }
    return columns
  }
  /**
   * `columnWidths` permette di assegnare larghezze fisse (CSS) a singole colonne.
   * - **Chiave**: nome della colonna (uguale alla chiave di `displayedColumns`).
   * - **Valore**: qualunque valore valido per `width` in CSS (es. `150px`, `10rem`, `20%`).
   *
   * Se una colonna non è presente in questo oggetto la larghezza verrà
   * determinata dal browser oppure, se la tabella è vuota, dal calcolo fatto
   * in `getColumnWidth()`.
   */
  @Input() columnWidths: { [key: string]: string } = {}
  /**
   * `ellipsisMaxWidth` imposta la larghezza massima (CSS `max-width`) del
   * contenitore testuale usato per mostrare l’ellissi `…` nelle celle che
   * hanno il tooltip abilitato.
   * Esempio: `"120px"` o `"15ch"`.
   */
  @Input() ellipsisMaxWidth = '100px'

  /**
   * `tooltipColumns` elenca le colonne per cui deve essere visualizzato un
   * `matTooltip` con il contenuto completo della cella.
   * Il confronto è in base al **nome colonna** usato in `displayedColumns`.
   */
  @Input() tooltipColumns: string[] = []
  /**
   * `filtersConfig` descrive (quando presente) i filtri da applicare sulla
   * tabella. Il tipo `FiltersConfig` è esterno e definisce:
   * - quali campi sono filtrabili;
   * - la logica di confronto (uguale, contiene, range, ecc.);
   * - eventuali operatori personalizzati.
   *
   * Il componente non applica direttamente i filtri: espone questa proprietà
   * perché un componente padre o un servizio possano leggerla per costruire
   * la UI dei filtri o per pre-popolarla.
   */
  @Input() filtersConfig?: FiltersConfig

  /**
   * `filterValues` contiene i valori correnti immessi dall’utente nei campi
   * di filtro.
   * La chiave è il nome del campo; il valore è tipizzato come `any` perché
   * può essere stringa, numero, data, ecc. a seconda del filtro.
   */
  filterValues: { [key: string]: any } = {}

  /**
   * `pageSize` definisce il numero di record da mostrare per pagina quando
   * è presente il `MatPaginator`.
   * - `0` (o valori falsy) ➜ paginazione disabilitata.
   * - `>0`             ➜ paginazione abilitata con quella dimensione.
   */
  @Input() pageSize = 0

  /**
   * `totalElements` opzionale per supportare la paginazione server-side.
   * Se specificato, viene usato per calcolare il numero totale di pagine
   * invece di `this.data.length`.
   * Utile quando i dati vengono caricati pagina per pagina dal backend.
   */
  @Input() totalElements?: number
  /**
   * `dataSource` è l’istanza di `MatTableDataSource` che Angular Material
   * usa internamente per gestire sorting, paginazione e filtraggio.
   * Viene sincronizzata a ogni cambio di `data` attraverso
   * `refreshDataSource()`.
   */
  dataSource = new MatTableDataSource<T>([])

  /**
   * `paginator` è il riferimento al componente `<mat-paginator>` dichiarato
   * nel template. Viene inizializzato in `ngAfterViewInit()` e collegato al
   * `dataSource` per consentire la paginazione automatica.
   */
  @ViewChild(MatPaginator) paginator!: MatPaginator

  /**
   * `sort` è il riferimento al direttiva `<mat-sort>` presente sulla tabella.
   * Collegandolo al `dataSource` si ottiene gratuitamente l’ordinamento
   * client-side.
   * Viene usato anche dal metodo `applyInitialSort()` per impostare la
   * colonna/direzione di default.
   */
  @Input() enableSorting = true
  @ViewChild(MatSort) sort!: MatSort
  /** Mittente attualmente selezionato (o null) */
  @Input() selectedSender: string | null = null
  /** Emesso quando si clicca un mittente */
  @Output() senderClick = new EventEmitter<string>()
  @Output() pageChange = new EventEmitter<number>()
  private _initialPage = 0

  @Input()
  set initialPage(value: number) {
    this._initialPage = value
  }

  ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.dataSource.data = this.data
      if (this.paginator) {
        // Se totalElements è passato esplicitamente, significa paginazione server-side
        // In quel caso NON colleghiamo dataSource.paginator per evitare che sovrascriva length
        if (this.totalElements === undefined) {
          // Client-side pagination: il paginator gestisce automaticamente i dati
          this.dataSource.paginator = this.paginator
        } else {
          // Server-side pagination: gestiamo manualmente la lunghezza
          this.paginator.length = this.totalElements
        }
        this.paginator.pageIndex = this._initialPage
        this.paginator._changePageSize(this.paginator.pageSize)
        this.paginator.page.subscribe(() => {
          this.pageChange.emit(this.currentPage)
        })
      }
      if (this.enableSorting) {
        this.dataSource.sort = this.sort
        this.setupCustomSortingDataAccessor()
      }
      if (!this._initialSort.column) {
        const first = Array.from(this.displayedColumns.keys())[0]
        this._initialSort = { column: first, direction: 'asc' }
      }

      this.applyInitialSort()
      this.refreshDataSource()
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      this.refreshDataSource()
    }
    if (changes['enableSorting'] && this.sort) {
      this.dataSource.sort = this.enableSorting ? this.sort : null!
      if (this.enableSorting) {
        this.setupCustomSortingDataAccessor()
      }
    }
    // Solo aggiorna pageIndex se initialPage è cambiato, non ogni volta che i dati cambiano
    if (changes['initialPage'] && !changes['initialPage'].firstChange) {
      Promise.resolve().then(() => {
        if (this.paginator) {
          this.paginator.pageIndex = this._initialPage
        }
      })
    }
    if (changes['totalElements']) {
      this.refreshDataSource()
    }
  }
  /**
   * `refreshDataSource()` sincronizza l'array `data` con `dataSource.data`
   * e, se presente il paginator, aggiorna anche la proprietà `length`
   * necessaria ad Angular Material per calcolare il numero di pagine.
   * Se `totalElements` è specificato (paginazione server-side), usa quello
   * invece di `this.data.length`.
   */
  private refreshDataSource() {
    this.dataSource.data = this.data
    // Per paginazione server-side, aggiorniamo manualmente la lunghezza
    if (this.paginator && this.pageChange.observed) {
      this.paginator.length = this.totalElements ?? this.data.length
    }
  }

  /**
   * `initialSort` permette al componente padre di specificare quale colonna
   * debba essere ordinata al primo render e con quale direzione.
   *
   * Esempio di binding:
   * ```html
   * <app-generic-table
   *   [initialSort]="{ column: 'createdAt', direction: 'desc' }">
   * </app-generic-table>
   * ```
   *
   * Se non viene passato nulla, il componente usa `_initialSort` che a sua
   * volta viene impostato (in `ngAfterViewInit`) sulla **prima colonna** della
   * mappa `displayedColumns`.
   */
  @Input()
  set initialSort(value: { column: string; direction: SortDirection }) {
    this._initialSort = value ?? this._initialSort
    if (this.sort) {
      this.applyInitialSort()
    }
  }
  /**
   * `_initialSort` contiene il valore effettivo usato per l’ordinamento
   * iniziale. È separato da `initialSort` per consentire un setter `@Input`
   * che reagisca ai cambi senza perdere il valore di fallback.
   */
  private _initialSort: { column: string; direction: SortDirection } = {
    column: 'id',
    direction: 'asc'
  }
  /**
   * `applyInitialSort()` applica il sort definito in `_initialSort`
   * impostando `sort.active`/`sort.direction` e forzando l’emissione
   * dell’evento `sortChange` per aggiornare le intestazioni della tabella.
   */
  private applyInitialSort(): void {
    if (!this.enableSorting) return
    const { column, direction } = this._initialSort
    this.sort.active = column
    this.sort.direction = direction
    this.sort.sortChange.emit({ active: column, direction })
  }

  /**
   * `getColumnWidth()` restituisce la larghezza (stringa CSS) da associare
   * a una colonna:
   * - **Tabella vuota** ➜ tutte le colonne hanno la stessa percentuale
   *   (`100 / numeroColonne %`).
   * - **Tabella con dati** ➜ valore da `columnWidths` o `'auto'` se assente.
   *
   * @param col Nome della colonna.
   */
  public getColumnWidth(col: string): string {
    const total = this.allColumns.length
    if (this.dataSource.data.length === 0) {
      return `${100 / total}%`
    }
    return this.columnWidths[col] ?? 'auto'
  }
  get currentPage(): number {
    return this.paginator?.pageIndex ?? 0
  }
  getSafeHtml(htmlString: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(htmlString)
  }

  /**
   * Setup del custom sorting data accessor per gestire date in formato italiano
   * e altri tipi di dati che richiedono ordinamento personalizzato
   */
  private setupCustomSortingDataAccessor(): void {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      const value = item[property]
      
      // Se il valore è una stringa che assomiglia a una data italiana (dd/MM/yyyy)
      if (typeof value === 'string' && this.isItalianDateFormat(value)) {
        return this.parseItalianDate(value)
      }
      
      // Per altri tipi di dato, usa il comportamento default
      return value
    }
  }

  /**
   * Verifica se una stringa è in formato data italiana (dd/MM/yyyy)
   */
  private isItalianDateFormat(dateString: string): boolean {
    // Regex per formato dd/MM/yyyy o dd/MM/yy
    const italianDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/
    return italianDateRegex.test(dateString)
  }

  /**
   * Converte una data in formato italiano (dd/MM/yyyy) in oggetto Date per il sorting
   */
  private parseItalianDate(dateString: string): Date {
    const parts = dateString.split('/')
    if (parts.length !== 3) {
      return new Date(0) // Data di fallback per ordinamento
    }
    
    const day = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1 // I mesi in JavaScript sono 0-indexed
    const year = parseInt(parts[2], 10)
    
    // Se l'anno è a 2 cifre, assumiamo che sia 20xx
    const fullYear = year < 100 ? 2000 + year : year
    
    const date = new Date(fullYear, month, day)
    
    // Verifica che la data sia valida
    if (date.getFullYear() === fullYear && 
        date.getMonth() === month && 
        date.getDate() === day) {
      return date
    }
    
    return new Date(0) // Data di fallback se la conversione fallisce
  }
}
